import { isPreviewOrigin } from "replo-utils/lib/url";

import { GOOGLE_CLIENT_ID } from "replo-utils/lib/googleAuth";

// (Patrick, 2026-03-12): Allowlist for origins that are permitted to receive
// Google credentials via the postMessage proxy flow.
// Matches:
// - Replo preview URLs: https://<slug>.preview.replointernal.com
// - localhost development: http://localhost:<port>
const LOCAL_DEV_ORIGIN_RE = /^http:\/\/localhost:\d+$/;

export function isAllowedReturnOrigin(origin: string): boolean {
  return isPreviewOrigin(origin) || LOCAL_DEV_ORIGIN_RE.test(origin);
}

export function buildGoogleOAuthUrl({
  returnOrigin,
  currentOrigin,
  nonce,
}: {
  returnOrigin: string;
  currentOrigin: string;
  nonce: string;
}): string {
  const authUrl = new URL("https://accounts.google.com/o/oauth2/v2/auth");
  authUrl.searchParams.set("client_id", GOOGLE_CLIENT_ID);
  authUrl.searchParams.set(
    "redirect_uri",
    `${currentOrigin}/auth/google-proxy`,
  );
  authUrl.searchParams.set("response_type", "id_token");
  authUrl.searchParams.set("scope", "openid email profile");
  authUrl.searchParams.set("nonce", nonce);
  authUrl.searchParams.set("state", returnOrigin);
  authUrl.searchParams.set("prompt", "select_account");
  return authUrl.toString();
}

type ProxyHashResult =
  | { type: "success"; credential: string; clientId: string; state: string }
  | { type: "error"; error: string; state: string | null }
  | { type: "invalid_origin"; error: string }
  | { type: "none" };

// (Patrick, 2026-03-12): Parses the URL hash fragment returned by Google OAuth.
// Returns a discriminated union so the component can handle each case.
export function parseOAuthHashResponse(hash: string): ProxyHashResult {
  if (hash.includes("id_token=")) {
    const params = new URLSearchParams(hash.slice(1));
    const idToken = params.get("id_token");
    const state = params.get("state");

    if (!state || !isAllowedReturnOrigin(state)) {
      return { type: "invalid_origin", error: "Invalid return origin." };
    }

    if (!idToken) {
      return {
        type: "error",
        error: "No credential received from Google.",
        state,
      };
    }

    return {
      type: "success",
      credential: idToken,
      clientId: GOOGLE_CLIENT_ID,
      state,
    };
  }

  if (hash.includes("error=")) {
    const params = new URLSearchParams(hash.slice(1));
    const errorMsg =
      params.get("error_description") ??
      params.get("error") ??
      "Google sign-in failed.";
    const state = params.get("state");
    return { type: "error", error: errorMsg, state };
  }

  return { type: "none" };
}
